import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { useState, useContext } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { RegistrationAnalytics } from '../../../constants';
import DocumentUploadApi from './DocumentUploadApi';
import validDocumentTypes from './validFileTypes';

type File = { name: string; size: number; type: string };

export const useDocumentUpload = () => {
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [loading, setLoading] = useState(false);
  const { trackEvent } = useContext(AnalyticsContext);

  const uploadDocument = (files: any, tags: any) => {
    const MAX_LENGTH = 22020096;

    for (let index = 0; index < files.length; index += 1) {
      // LATER: At this point file is either the file or { errors, file } if Dropzone caught error
      const file = files[index]?.file || files[index];

      if (file && file.size && file.size > MAX_LENGTH) {
        setUploadedFiles({
          ...uploadedFiles,
          [file.name]: {
            file,
            progress: 0,
            error: 'SIZE',
            warning: false,
          },
        });
        // eslint-disable-next-line no-prototype-builtins
      } else if (validDocumentTypes.hasOwnProperty(file.type)) {
        setLoading(true);
        DocumentUploadApi.uploadDocument(file, tags)
          .then(() => {
            setUploadedFiles({
              ...uploadedFiles,
              [file.name]: {
                file,
                progress: 1,
                error: false,
                warning: false,
              },
            });
            datadogRum.addAction('Document Uploaded', {
              fileName: file.name,
            });
          })
          .catch((err) => {
            setUploadedFiles({
              ...uploadedFiles,
              [file.name]: {
                file,
                progress: 0,
                error: false,
                warning: true,
              },
            });
            datadogRum.addAction('Document Upload Catch Failed', {
              fileName: file.name,
              err,
            });
          })
          .finally(() => {
            setLoading(false);
            datadogRum.addAction('Document Upload Complete', {
              fileName: file.name,
            });
          });
      } else {
        setUploadedFiles({
          ...uploadedFiles,
          [file.name]: {
            file,
            progress: 0,
            error: 'TYPE',
            warning: false,
          },
        });
        datadogRum.addAction('Document Upload Failed', {
          fileName: file.name,
        });
      }
    }
  };

  const removeDocument = (file: File, documentType: string) => {
    const { [file.name]: removedFile, ...restFiles } = uploadedFiles as any;
    setUploadedFiles(restFiles);
    trackEvent(RegistrationAnalytics.REMOVED_FILE, {
      documentType,
      error: removedFile.error,
    });
  };

  return {
    uploadDocument,
    removeDocument,
    uploadedFiles,
    loading,
  };
};
